import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send, Instagram, Facebook, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import { MAPS_DIRECTIONS, PHONE_1, PHONE_2 } from "@/i18n/translations";
import { OpeningHours } from "@/components/OpeningHours";
import { ConsentMap } from "@/components/ConsentMap";
import api from "@/lib/api";
import { track } from "@/lib/analytics";

const MAP_SRC =
  "https://www.google.com/maps?q=Smolensky+1,+Keratsini+18756,+Greece&output=embed";

export const ContactSection = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.message) {
      toast.error(c.error);
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact-messages", {
        first_name: form.first_name,
        last_name: form.last_name || null,
        email: form.email,
        message: form.message,
      });
      track("contact_message", { fb_event: "Contact" });
      toast.success(c.success);
      setForm({ first_name: "", last_name: "", email: "", message: "" });
    } catch {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  const field = "rounded-xl border-brand/20 bg-white focus-visible:ring-brand h-11";

  return (
    <section id="contact" className="relative py-24 sm:py-32 bg-white" data-testid="contact">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-bamboo">{c.tag}</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink tracking-tight">{c.title}</h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Address — opens Google Maps directions */}
              <a
                href={MAPS_DIRECTIONS}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-address-link"
                onClick={() => track("directions_click", { fb_event: "Contact", location: "contact" })}
                className="group rounded-2xl bg-brand-cream border border-brand/10 p-5 hover:border-brand transition-colors no-a11y-underline"
              >
                <span className="grid place-items-center h-10 w-10 rounded-full bg-brand text-brand-cream mb-3">
                  <MapPin className="h-5 w-5" />
                </span>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50">{c.address_label}</p>
                <p className="mt-1 font-semibold text-brand-ink text-sm leading-snug">{c.address}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-bamboo-deep opacity-0 group-hover:opacity-100 transition-opacity">
                  <Navigation className="h-3.5 w-3.5" /> {c.get_directions}
                </span>
              </a>

              {/* Phones — click to call */}
              <div className="rounded-2xl bg-brand-cream border border-brand/10 p-5" data-testid="contact-phones">
                <span className="grid place-items-center h-10 w-10 rounded-full bg-brand text-brand-cream mb-3">
                  <Phone className="h-5 w-5" />
                </span>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50">{c.phone_label}</p>
                <div className="mt-1 flex flex-col gap-0.5">
                  <a href={`tel:+30${PHONE_1}`} data-testid="contact-phone-1" onClick={() => track("call_click", { fb_event: "Contact", number: PHONE_1, location: "contact" })} className="font-semibold text-brand-ink text-sm hover:text-brand transition-colors">{PHONE_1}</a>
                  <a href={`tel:+30${PHONE_2}`} data-testid="contact-phone-2" onClick={() => track("call_click", { fb_event: "Contact", number: PHONE_2, location: "contact" })} className="font-semibold text-brand-ink text-sm hover:text-brand transition-colors">{PHONE_2}</a>
                </div>
              </div>

              {/* Email */}
              <a
                href="mailto:care4me.gr@gmail.com"
                data-testid="contact-email-link"
                onClick={() => track("email_click", { fb_event: "Contact", location: "contact" })}
                className="rounded-2xl bg-brand-cream border border-brand/10 p-5 hover:border-brand transition-colors no-a11y-underline sm:col-span-2"
              >
                <span className="grid place-items-center h-10 w-10 rounded-full bg-brand text-brand-cream mb-3">
                  <Mail className="h-5 w-5" />
                </span>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50">{c.email_label}</p>
                <p className="mt-1 font-semibold text-brand-ink text-sm leading-snug">care4me.gr@gmail.com</p>
              </a>
            </div>

            <OpeningHours />

            <div className="rounded-2xl overflow-hidden border border-brand/10 h-[240px]">
              <ConsentMap src={MAP_SRC} title="Care For Me location" testid="contact-map" />
            </div>

            <div>
              <p className="text-sm font-bold text-brand-ink mb-3">{c.follow}</p>
              <div className="flex gap-3">
                <a href="https://www.instagram.com/careforme.gr/" target="_blank" rel="noreferrer" aria-label="Instagram" className="grid place-items-center h-11 w-11 rounded-full bg-brand-soft/60 text-brand hover:bg-brand hover:text-brand-cream transition-colors duration-200">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61588405945105&locale=el_GR" target="_blank" rel="noreferrer" aria-label="Facebook" className="grid place-items-center h-11 w-11 rounded-full bg-brand-soft/60 text-brand hover:bg-brand hover:text-brand-cream transition-colors duration-200">
                  <Facebook className="h-5 w-5" />
                </a>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-form-name="contact"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[2rem] bg-brand-cream border border-brand/10 p-7 sm:p-9 shadow-[0_16px_50px_rgba(134,77,99,0.08)]"
            data-testid="contact-form"
          >
            <h3 className="font-heading text-2xl font-bold text-brand-ink mb-6">{c.form_title}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-brand-ink font-semibold">{c.first_name} *</Label>
                <Input data-testid="c-first" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={field} />
              </div>
              <div>
                <Label className="text-brand-ink font-semibold">{c.last_name}</Label>
                <Input data-testid="c-last" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={field} />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-brand-ink font-semibold">{c.email} *</Label>
              <Input data-testid="c-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={field} />
            </div>
            <div className="mt-4">
              <Label className="text-brand-ink font-semibold">{c.message} *</Label>
              <Textarea data-testid="c-message" value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-xl border-brand/20 bg-white focus-visible:ring-brand min-h-[130px]" />
            </div>
            <Button data-testid="contact-submit-btn" type="submit" disabled={loading} className="mt-6 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 text-base no-a11y-underline">
              <Send className="h-4 w-4 mr-2" />
              {loading ? c.sending : c.send}
            </Button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
